/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleWorkflow } from 'features/workflows/workflowsSlice'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import { workflowCriteriaList } from 'pages/Operations/Plans/plansData'
import { components } from 'pages/TaskTemplates/taskData'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, Separator, ThinSeparator } from 'styling/GlobalStyles'
import { BodyText } from 'styling/Typography'
import { IWorkflowsSingleResponse } from 'types/api/workflowsAPi.interface'
import { IPhaseState, IWorkflowBindingConfigState } from 'types/operationPlans.interface'

type WorkflowTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<IWorkflowBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<IWorkflowBindingConfigState>>>
}

export const WorkflowTemplateType = ({ formKey, addingPhase, setAddingPhase }: WorkflowTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as IWorkflowBindingConfigState)
    const items = bindingConfig.items ?? []

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<IWorkflowBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as IWorkflowBindingConfigState,
        }))
    }

    const handleWorkflowSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleWorkflow(value.value))
        if (resp.meta.requestStatus === 'fulfilled') {
            // @ts-ignore
            setAddingPhase((prev) => ({
                ...prev,
                workflowTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    criteria: null,
                    items: (resp.payload as IWorkflowsSingleResponse).items.map((item) => ({
                        workflowTemplateItem: { label: item.taskTemplate.name, value: item.id },
                        location: null,
                        componentValues: item.taskTemplate.components.map((c) => ({
                            taskComponent: { label: c.name, value: c.id },
                            componentType: c.component,
                            value: null,
                        })),
                        toolComponentValues: item.taskTemplate.toolTypeComponents.map((tc) => ({
                            taskTemplateToolTypeComponent: { value: tc.id, label: tc.name },
                            toolComponent: { label: tc.toolTypeComponent.name, value: tc.toolTypeComponent.id },
                            amount: null,
                        })),
                    })),
                },
            }))
        }
    }

    const handleComponentInput = (e: React.ChangeEvent<HTMLInputElement>, itemIndex: number, componentIndex: number) => {
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        const config = addingPhase.bindingConfig
        if (!config?.items) return
        updateBindingConfig({
            items: config.items.map((item, i) =>
                i === itemIndex
                    ? {
                        ...item,
                        componentValues: item.componentValues.map((c, j) =>
                            j === componentIndex ? { ...c, value: newValue } : c,
                        ),
                    }
                    : item,
            ),
        })
    }

    const handleComponentSelect = (value: ISelectValue, itemIndex: number, componentIndex: number) => {
        const config = addingPhase.bindingConfig
        if (!config?.items) return
        updateBindingConfig({
            items: config.items.map((item, i) =>
                i === itemIndex
                    ? {
                        ...item,
                        componentValues: item.componentValues.map((c, j) =>
                            j === componentIndex ? { ...c, value } : c,
                        ),
                    }
                    : item,
            ),
        })
    }

    const handleComponentDate = (date: Date, itemIndex: number, componentIndex: number) => {
        const config = addingPhase.bindingConfig
        if (!config?.items) return
        updateBindingConfig({
            items: config.items.map((item, i) =>
                i === itemIndex
                    ? {
                        ...item,
                        componentValues: item.componentValues.map((c, j) =>
                            j === componentIndex ? { ...c, value: date } : c,
                        ),
                    }
                    : item,
            ),
        })
    }

    const handleToolComponentValue = (e: React.ChangeEvent<HTMLInputElement>, itemIndex: number, componentIndex: number) => {
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        const config = addingPhase.bindingConfig
        if (!config?.items) return
        updateBindingConfig({
            items: config.items.map((item, i) =>
                i === itemIndex
                    ? {
                        ...item,
                        toolComponentValues: item.toolComponentValues.map((tc, j) =>
                            j === componentIndex ? { ...tc, amount: newValue } : tc,
                        ),
                    }
                    : item,
            ),
        })
    }

    const handleLocationChange = (value: ISelectValue, itemIndex: number) => {
        const config = addingPhase.bindingConfig
        if (!config?.items) return
        updateBindingConfig({
            items: config.items.map((item, i) => (i === itemIndex ? { ...item, location: value } : item)),
        })
    }

    const renderComponentInput = (component: IComponent, j: number, i: number) => {
        const foundRenderInput = components.find(c => c.component === component.componentType)

        let value;
        const fn = foundRenderInput?.inputType == 3 ? handleComponentDate : [4, 5].includes(foundRenderInput?.inputType) ? handleComponentSelect : handleComponentInput

        switch (foundRenderInput?.inputType) {
            case 1:
                value = component.value
                break
            case 2:
                value = Number(component.value)
                break
            case 3:
                value = new Date(String(component.value))
                break
            case 4:
                value = (component.value as ISelectValue).value
                break
            case 5:
                value = (component.value as ISelectValue).label
        }

        return foundRenderInput?.renderInput({ handleChange: (e) => fn(e, i, j), value, name: 'value', labelText: component?.taskComponent?.label })
    }

    return <>
        <Flex column gap='1rem' >
            <HonestSelectAsyncInput
                key={`workflow-template-${formKey}`}
                keyProp={3}
                defaultValue={addingPhase.workflowTemplate?.label}
                link='workflow/template'
                labelText='Workflow Template'
                onChange={handleWorkflowSelect}
                name='workflowTemplate'
                placeholder='Search Workflow Templates'
                parserFunction={parse}
                required
                invalid={!addingPhase.workflowTemplate?.value}
            />
            {addingPhase.workflowTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`workflow-startDate-${formKey}`}
                        labelText='Start Date'
                        minDate={new Date()}
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                    <HonestSelectInput
                        key={`workflow-criteria-${formKey}`}
                        name='criteria'
                        labelText='Criteria'
                        options={workflowCriteriaList}
                        defaultValue={bindingConfig.criteria?.value}
                        onChange={(value: ISelectValue) => updateBindingConfig({ criteria: value })}
                    />
                </Flex>

                <Separator />
                {items.map((item, i) => (
                    <Flex column gap='1rem' key={`${formKey}-item-${i}`}>
                        <BodyText>{item.workflowTemplateItem?.label}</BodyText>
                        <HonestSelectAsyncInput
                            key={`workflow-location-${formKey}-${i}`}
                            keyProp={3}
                            link='locations'
                            placeholder='Search locations'
                            name='location'
                            labelText='Location'
                            defaultValue={item.location?.label}
                            onChange={(value) => handleLocationChange(value, i)}
                            parserFunction={parseLocationRegistryOptions}
                        />
                        <ItemsGrid columns={2} >
                            {item.componentValues.map((c, j) => renderComponentInput(c, j, i))}
                        </ItemsGrid>
                        <ItemsGrid columns={2} >
                            {item.toolComponentValues.map((c, j) => (
                                <Input
                                    value={c.amount ?? ''}
                                    key={j}
                                    width='100%'
                                    placeholder='Enter value'
                                    onChange={(e) => handleToolComponentValue(e, i, j)}
                                    labelText={c.taskTemplateToolTypeComponent.label + ' value'}
                                    name='value'
                                    type='number'
                                />
                            ))}
                        </ItemsGrid>
                        <ThinSeparator />
                    </Flex>
                ))}
            </>}
        </Flex>

    </>
}


interface IComponent {
    taskComponent: ISelectValue;
    componentType: number;
    value: number | Date | ISelectValue | string
}